import React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import {
    ACCOUNT_MENU_METRICS,
    ACCOUNT_ROLE_ICONS,
    accountTypography,
    describeAccountHolder,
} from '%evp_theme/brandbook/account/accountMenu';
import MaterialSymbol from '../MaterialSymbol';
import accountRoleTreatment from '../accountRoleTreatments';
import {
    ROLE_ADMINISTRATOR,
    ROLE_OPERATOR,
    ROLE_BUYER,
} from '../../services/readRedesignAccount';

const ROLE_LABEL_KEYS = Object.freeze({
    [ROLE_ADMINISTRATOR]: 'accountSignedInAsAdministrator',
    [ROLE_OPERATOR]: 'accountSignedInAsOperator',
    [ROLE_BUYER]: 'accountSignedInAsBuyer',
});

const {
    radius: MENU_RADIUS,
    badgeIconSize: ICON_SIZE,
    badgeSize: BADGE_SIZE,
    pointerSize: POINTER_SIZE,
    pointerOffsetFromRight: POINTER_OFFSET_FROM_RIGHT,
} = ACCOUNT_MENU_METRICS;

function buildStripSx(treatment, floating) {
    return {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 1.5,
        ...(floating ? {
            borderTopLeftRadius: `${MENU_RADIUS}px`,
            borderTopRightRadius: `${MENU_RADIUS}px`,
        } : null),
        bgcolor: treatment.bgcolor,
        color: treatment.color,
    };
}

function buildPointerSx(treatment) {
    return {
        position: 'absolute',
        top: -POINTER_SIZE,
        right: POINTER_OFFSET_FROM_RIGHT,
        width: 0,
        height: 0,
        borderLeft: `${POINTER_SIZE}px solid transparent`,
        borderRight: `${POINTER_SIZE}px solid transparent`,
        borderBottom: `${POINTER_SIZE}px solid`,
        borderBottomColor: treatment.bgcolor,
    };
}

function buildBadgeSx(treatment) {
    return {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        width: BADGE_SIZE,
        height: BADGE_SIZE,
        borderRadius: '50%',
        bgcolor: treatment.badgeBackground,
        color: treatment.color,
    };
}

const IDENTITY_SX = {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
};

const HOLDER_TEXT_SX = {
    overflowWrap: 'anywhere',
    color: 'inherit',
};

const HOLDER_NAME_SX = theme => ({
    ...HOLDER_TEXT_SX,
    ...accountTypography(theme, 'holderName'),
});

const HOLDER_EMAIL_SX = theme => ({
    ...HOLDER_TEXT_SX,
    ...accountTypography(theme, 'holderEmail'),
});

const LABEL_SX = theme => ({
    minWidth: 0,
    overflowWrap: 'anywhere',
    color: 'inherit',
    ...accountTypography(theme, 'roleLabel'),
});

function AccountRoleStrip({
    role,
    translations,
    fullName = '',
    email = '',
    floating = false,
}) {
    const treatment = accountRoleTreatment(role, useTheme());
    const holder = describeAccountHolder(fullName, email);

    return (
        <Box
            role="note"
            sx={buildStripSx(treatment, floating)}
            data-testid="redesign-account-role-strip"
            data-role={role}
        >
            {floating ? (
                <Box aria-hidden="true" sx={buildPointerSx(treatment)} data-testid="redesign-account-role-pointer" />
            ) : null}
            <Box sx={buildBadgeSx(treatment)} data-testid="redesign-account-role-badge">
                <MaterialSymbol name={ACCOUNT_ROLE_ICONS[role].symbol} size={ICON_SIZE} />
            </Box>
            <Box sx={IDENTITY_SX}>
                {holder.primary === '' ? null : (
                    <Box component="span" sx={HOLDER_NAME_SX} data-testid="redesign-account-holder-primary">
                        {holder.primary}
                    </Box>
                )}
                {holder.secondary === '' ? null : (
                    <Box component="span" sx={HOLDER_EMAIL_SX} data-testid="redesign-account-holder-secondary">
                        {holder.secondary}
                    </Box>
                )}
                <Box component="span" sx={LABEL_SX}>{translations[ROLE_LABEL_KEYS[role]]}</Box>
            </Box>
        </Box>
    );
}

AccountRoleStrip.propTypes = {
    role: PropTypes.oneOf([ROLE_ADMINISTRATOR, ROLE_OPERATOR, ROLE_BUYER]).isRequired,
    translations: PropTypes.shape({
        accountSignedInAsAdministrator: PropTypes.string.isRequired,
        accountSignedInAsOperator: PropTypes.string.isRequired,
        accountSignedInAsBuyer: PropTypes.string.isRequired,
    }).isRequired,
    fullName: PropTypes.string,
    email: PropTypes.string,
    floating: PropTypes.bool,
};

export default AccountRoleStrip;
